// Block-based marketing email documents ("Designer" mode).
//
// The document is the editor's source of truth and lives in `contentJson`
// (campaign variants, journey SEND_EMAIL configs, and email templates).
// `renderMarketingEmailDocument` serializes it to email-client-safe HTML
// (tables + inline CSS) which is stored in `contentHtml` — the send pipeline,
// tracking, sanitizer, and personalization all keep operating on the HTML and
// never need to know blocks exist.
//
// Serializer constraints (MarketingContentPolicyService allowlist):
// - allowlisted tags/attributes only; no <style> blocks (also Gmail-stripped),
//   no url() in inline CSS, https-only image sources.
// - Output is a body fragment: the sender wraps it in the 600px card and
//   appends the compliance footer. 552px = card width minus its 24px padding.
// - {{token}} personalization is substituted at send time on the HTML, so
//   tokens typed into headings/text/buttons work unchanged.

export type MarketingEmailBlockAlign = 'left' | 'center' | 'right'

export interface MarketingEmailHeadingBlock {
  id: string
  type: 'heading'
  level: 1 | 2 | 3
  text: string
  align?: MarketingEmailBlockAlign
  color?: string
}

export interface MarketingEmailTextBlock {
  id: string
  type: 'text'
  /** TipTap-produced rich text HTML (paragraphs, lists, links, color spans) */
  html: string
}

export interface MarketingEmailImageBlock {
  id: string
  type: 'image'
  /** Permanent https URL (public-asset CDN upload or external) */
  url: string
  alt: string
  /** Percent of the content width (25–100) */
  width?: number
  align?: MarketingEmailBlockAlign
  linkUrl?: string
}

export interface MarketingEmailButtonBlock {
  id: string
  type: 'button'
  label: string
  url: string
  align?: MarketingEmailBlockAlign
  backgroundColor?: string
  textColor?: string
  fullWidth?: boolean
}

export interface MarketingEmailDividerBlock {
  id: string
  type: 'divider'
}

export interface MarketingEmailSpacerBlock {
  id: string
  type: 'spacer'
  /** Height in px */
  height?: number
}

/** Blocks allowed inside a column or section (no nested containers). */
export type MarketingEmailLeafBlock =
  | MarketingEmailHeadingBlock
  | MarketingEmailTextBlock
  | MarketingEmailImageBlock
  | MarketingEmailButtonBlock
  | MarketingEmailDividerBlock
  | MarketingEmailSpacerBlock

export interface MarketingEmailColumnsBlock {
  id: string
  type: 'columns'
  left: MarketingEmailLeafBlock[]
  right: MarketingEmailLeafBlock[]
}

/**
 * A full-width colored band (hero, callout, footer strip) containing other
 * blocks. Text inside auto-contrasts: dark backgrounds get light base text.
 */
export interface MarketingEmailSectionBlock {
  id: string
  type: 'section'
  backgroundColor?: string
  /** Inner padding in px (default 24) */
  padding?: number
  blocks: MarketingEmailLeafBlock[]
}

export type MarketingEmailBlock =
  | MarketingEmailLeafBlock
  | MarketingEmailColumnsBlock
  | MarketingEmailSectionBlock

export interface MarketingEmailDocumentSettings {
  /** Canvas color behind the email card */
  backgroundColor?: string
  /** Card (email body) background color */
  cardColor?: string
}

export interface MarketingEmailDocument {
  version: 1
  settings?: MarketingEmailDocumentSettings
  blocks: MarketingEmailBlock[]
}

export const MARKETING_EMAIL_BLOCK_TYPES = [
  'heading',
  'text',
  'image',
  'button',
  'divider',
  'spacer',
  'columns',
  'section',
] as const

export function createEmptyMarketingEmailDocument(): MarketingEmailDocument {
  return { version: 1, blocks: [] }
}

/**
 * Loose structural guard for contentJson payloads: anything else stored there
 * (legacy TipTap JSON, null) falls back to rich-text mode.
 */
export function isMarketingEmailDocument(
  value: unknown,
): value is MarketingEmailDocument {
  if (!value || typeof value !== 'object') return false
  const candidate = value as { version?: unknown; blocks?: unknown }
  return candidate.version === 1 && Array.isArray(candidate.blocks)
}

// ---------------------------------------------------------------------------
// Serializer
// ---------------------------------------------------------------------------

/** Content width inside the sender's 600px card (24px padding per side). */
const CONTENT_WIDTH_PX = 552

const HEADING_FONT_SIZE: Record<1 | 2 | 3, string> = {
  1: '26px',
  2: '21px',
  3: '17px',
}

export const MARKETING_EMAIL_BUTTON_DEFAULT_BG = '#0066cc'
export const MARKETING_EMAIL_BUTTON_DEFAULT_TEXT = '#ffffff'
export const MARKETING_EMAIL_DEFAULT_BACKGROUND = '#f6f6f6'
export const MARKETING_EMAIL_DEFAULT_CARD = '#ffffff'

/**
 * Perceived-luminance dark check. Hex colors only (3/6 digit) — anything else
 * (named colors, invalid input) is treated as light, which only costs a
 * light-text fallback, never unreadable output.
 */
export function isMarketingEmailColorDark(
  color: string | null | undefined,
): boolean {
  const match = color?.trim().match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
  if (!match) return false
  let hex = match[1]
  if (hex.length === 3)
    hex = hex
      .split('')
      .map(char => char + char)
      .join('')
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55
}

export interface MarketingEmailResolvedColors {
  backgroundColor: string
  cardColor: string
  /** Base body text on the card */
  textColor: string
  /** Muted text (footer, secondary lines) readable on the card */
  mutedColor: string
  /** Hairline/divider color readable on the card */
  borderColor: string
}

/**
 * Resolve the effective canvas/card/text colors for a document (or defaults
 * when it isn't one). Shared by the designer canvas, gallery previews, and
 * the send wrapper so all three always agree.
 */
export function resolveMarketingEmailColors(
  document?: { settings?: MarketingEmailDocumentSettings } | null,
): MarketingEmailResolvedColors {
  const backgroundColor = safeColor(
    document?.settings?.backgroundColor,
    MARKETING_EMAIL_DEFAULT_BACKGROUND,
  )
  const cardColor = safeColor(
    document?.settings?.cardColor,
    MARKETING_EMAIL_DEFAULT_CARD,
  )
  const dark = isMarketingEmailColorDark(cardColor)
  return {
    backgroundColor,
    cardColor,
    textColor: dark ? '#f5f5f5' : '#1a1a1a',
    mutedColor: dark ? '#a3a3a3' : '#8a8a8a',
    borderColor: dark ? '#3f3f46' : '#e5e5e5',
  }
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Allow only colors that can never smuggle CSS (hex or simple names). */
function safeColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback
  return /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]+)$/.test(value.trim())
    ? value.trim()
    : fallback
}

function safeUrl(value: string | undefined): string | null {
  const trimmed = value?.trim()
  if (!trimmed) return null
  return /^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed)
    ? trimmed
    : null
}

/** Ambient rendering context: available width + inherited contrast colors. */
interface RenderCtx {
  contentWidth: number
  textColor: string
  borderColor: string
}

function renderHeading(
  block: MarketingEmailHeadingBlock,
  ctx: RenderCtx,
): string {
  const tag = `h${block.level}`
  const color = safeColor(block.color, ctx.textColor)
  const align = block.align ?? 'left'
  return `<${tag} style="margin:0 0 16px;font-size:${HEADING_FONT_SIZE[block.level]};line-height:1.3;text-align:${align};color:${color};">${escapeHtml(block.text)}</${tag}>`
}

function renderText(block: MarketingEmailTextBlock): string {
  return `<div style="margin:0 0 16px;">${block.html}</div>`
}

function renderImage(block: MarketingEmailImageBlock, ctx: RenderCtx): string {
  const src = safeUrl(block.url)
  if (!src || !/^https:\/\//i.test(src)) return ''
  const percent = Math.min(100, Math.max(25, block.width ?? 100))
  const widthPx = Math.round((ctx.contentWidth * percent) / 100)
  const align = block.align ?? 'center'
  const img = `<img src="${escapeHtml(src)}" alt="${escapeHtml(block.alt)}" width="${widthPx}" style="display:inline-block;max-width:100%;height:auto;border:0;" />`
  const link = safeUrl(block.linkUrl)
  const inner = link
    ? `<a href="${escapeHtml(link)}" target="_blank" rel="noopener noreferrer">${img}</a>`
    : img
  return `<div style="margin:0 0 16px;text-align:${align};">${inner}</div>`
}

function renderButton(block: MarketingEmailButtonBlock): string {
  const href = safeUrl(block.url) ?? 'https://example.com'
  const background = safeColor(
    block.backgroundColor,
    MARKETING_EMAIL_BUTTON_DEFAULT_BG,
  )
  const color = safeColor(block.textColor, MARKETING_EMAIL_BUTTON_DEFAULT_TEXT)
  const align = block.align ?? 'center'
  const width = block.fullWidth ? ' width="100%"' : ''
  const tableAlign = block.fullWidth ? '' : ` align="${align}"`
  const wrapperAlign = block.fullWidth ? 'center' : align
  return `<div style="margin:0 0 16px;text-align:${wrapperAlign};"><table role="presentation" border="0" cellpadding="0" cellspacing="0"${tableAlign}${width} style="border-collapse:separate;"><tbody><tr><td bgcolor="${background}" align="center" style="border-radius:6px;background-color:${background};"><a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:12px 24px;font-size:15px;font-weight:600;color:${color};text-decoration:none;">${escapeHtml(block.label)}</a></td></tr></tbody></table></div>`
}

function renderDivider(ctx: RenderCtx): string {
  return `<hr style="border:none;border-top:1px solid ${ctx.borderColor};margin:24px 0;" />`
}

function renderSpacer(block: MarketingEmailSpacerBlock): string {
  const height = Math.min(120, Math.max(4, block.height ?? 24))
  return `<div style="height:${height}px;line-height:${height}px;font-size:1px;">&nbsp;</div>`
}

function renderLeafBlock(
  block: MarketingEmailLeafBlock,
  ctx: RenderCtx,
): string {
  switch (block.type) {
    case 'heading':
      return renderHeading(block, ctx)
    case 'text':
      return renderText(block)
    case 'image':
      return renderImage(block, ctx)
    case 'button':
      return renderButton(block)
    case 'divider':
      return renderDivider(ctx)
    case 'spacer':
      return renderSpacer(block)
    default:
      return ''
  }
}

function renderColumns(block: MarketingEmailColumnsBlock, ctx: RenderCtx): string {
  // Fixed 50/50 split. Media-query stacking is off the table: the sanitizer
  // (and Gmail) strip <style> blocks, so columns stay side-by-side on mobile.
  const columnCtx: RenderCtx = {
    ...ctx,
    contentWidth: Math.floor((ctx.contentWidth - 24) / 2),
  }
  const renderColumn = (blocks: MarketingEmailLeafBlock[], side: 'l' | 'r') =>
    `<td width="50%" valign="top" style="padding-${side === 'l' ? 'right' : 'left'}:12px;">${blocks
      .map(child => renderLeafBlock(child, columnCtx))
      .join('')}</td>`
  return `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin:0 0 16px;border-collapse:collapse;"><tbody><tr>${renderColumn(block.left, 'l')}${renderColumn(block.right, 'r')}</tr></tbody></table>`
}

export function marketingEmailSectionPadding(
  block: Pick<MarketingEmailSectionBlock, 'padding'>,
): number {
  return Math.min(64, Math.max(0, block.padding ?? 24))
}

function renderSection(block: MarketingEmailSectionBlock, ctx: RenderCtx): string {
  const padding = marketingEmailSectionPadding(block)
  const background = block.backgroundColor
    ? safeColor(block.backgroundColor, '')
    : ''
  const dark = isMarketingEmailColorDark(background)
  const sectionCtx: RenderCtx = {
    contentWidth: ctx.contentWidth - padding * 2,
    textColor: background ? (dark ? '#f5f5f5' : '#1a1a1a') : ctx.textColor,
    borderColor: background ? (dark ? '#52525b' : '#d4d4d8') : ctx.borderColor,
  }
  const bgAttrs = background
    ? ` bgcolor="${background}"`
    : ''
  const bgStyle = background ? `background-color:${background};` : ''
  // color on the td so plain text-block paragraphs inherit readable contrast
  return `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin:0 0 16px;border-collapse:separate;"><tbody><tr><td${bgAttrs} style="${bgStyle}border-radius:8px;padding:${padding}px;color:${sectionCtx.textColor};">${block.blocks
    .map(child => renderLeafBlock(child, sectionCtx))
    .join('')}</td></tr></tbody></table>`
}

/**
 * Serialize a block document to the email-safe HTML fragment stored in
 * contentHtml. Guaranteed to pass MarketingContentPolicyService.sanitizeHtml
 * unchanged (covered by test tooling; text-block HTML is TipTap output and is
 * additionally sanitized server-side like all campaign HTML).
 */
export function renderMarketingEmailDocument(
  document: MarketingEmailDocument,
): string {
  const colors = resolveMarketingEmailColors(document)
  const ctx: RenderCtx = {
    contentWidth: CONTENT_WIDTH_PX,
    textColor: colors.textColor,
    borderColor: colors.borderColor,
  }
  return document.blocks
    .map(block =>
      block.type === 'columns'
        ? renderColumns(block, ctx)
        : block.type === 'section'
          ? renderSection(block, ctx)
          : renderLeafBlock(block, ctx),
    )
    .join('\n')
}
